/**
 * Flow history tools — undo a bad change, and replay a real trigger payload.
 *
 * Power Automate keeps every saved version of a flow server-side. `list_flow_versions`
 * already surfaces them; these tools close the loop by reading one back and by
 * restoring it. `get_trigger_inputs` pulls the payload a past run actually
 * fired with, so a fix can be tested against the data that broke it rather
 * than something hand-written.
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { UpdateFlowRequest } from "../api/types.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

const FLOW_ID_DESCRIPTION = "The flow ID (GUID)";
const VERSION_ID_DESCRIPTION =
  "The version ID from list_flow_versions (an opaque alphanumeric string, not a number)";

// =============================================================================
// Tool definitions
// =============================================================================

export const getFlowVersionTool = {
  name: "get_flow_version",
  description:
    "Read one saved version of a flow, including its full definition — inspect what a flow looked like before a change, or check a version before restoring it.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: { type: "string", description: FLOW_ID_DESCRIPTION },
      versionId: { type: "string", description: VERSION_ID_DESCRIPTION },
      environment: { type: "string", description: "Environment name or GUID. Uses default if omitted." },
      format: {
        type: "string",
        enum: ["summary", "json"],
        description: "summary (default) or the raw definition JSON",
      },
    },
    required: ["flowId", "versionId"],
  },
};

export const restoreFlowVersionTool = {
  name: "restore_flow_version",
  description:
    "Roll a flow back to a previous saved version — the undo for a bad edit. Previews the change unless confirm=true. The current definition is itself saved as a version first, so a restore can be undone.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: { type: "string", description: FLOW_ID_DESCRIPTION },
      versionId: { type: "string", description: VERSION_ID_DESCRIPTION },
      environment: { type: "string", description: "Environment name or GUID. Uses default if omitted." },
      confirm: {
        type: "boolean",
        description: "Set true to apply the rollback; omit to preview what would change",
      },
    },
    required: ["flowId", "versionId"],
  },
};

export const getTriggerInputsTool = {
  name: "get_trigger_inputs",
  description:
    "Get the trigger payload a past run actually fired with — the real inputs, not a hand-written guess. Feed it back through test_flow to reproduce a failure against the data that caused it.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: { type: "string", description: FLOW_ID_DESCRIPTION },
      runId: { type: "string", description: "The run ID from get_runs" },
      environment: { type: "string", description: "Environment name or GUID. Uses default if omitted." },
    },
    required: ["flowId", "runId"],
  },
};

export const flowHistoryTools = [getFlowVersionTool, restoreFlowVersionTool, getTriggerInputsTool];

// =============================================================================
// Input schemas
// =============================================================================

const getVersionSchema = z.object({
  flowId: z.string().min(1),
  versionId: z.string().min(1),
  environment: z.string().optional(),
  format: z.enum(["summary", "json"]).optional().default("summary"),
});

const restoreSchema = z.object({
  flowId: z.string().min(1),
  versionId: z.string().min(1),
  environment: z.string().optional(),
  confirm: z.boolean().optional().default(false),
});

const triggerInputsSchema = z.object({
  flowId: z.string().min(1),
  runId: z.string().min(1),
  environment: z.string().optional(),
});

// =============================================================================
// Handlers
// =============================================================================

export async function handleGetFlowVersion(
  api: FlowManagementApi,
  input: unknown,
): Promise<string> {
  const parsed = getVersionSchema.parse(input);
  logger.info({ flowId: parsed.flowId, versionId: parsed.versionId }, "Executing get_flow_version");
  const version = await api.getFlowVersion(parsed.flowId, parsed.versionId, parsed.environment);

  if (parsed.format === "json") {
    return JSON.stringify(version.properties.definition, null, 2);
  }
  const def = version.properties.definition;
  return [
    `Version ${parsed.versionId} of "${version.properties.displayName}"`,
    `Created: ${version.properties.createdTime ?? "unknown"}`,
    `Triggers: ${Object.keys(def.triggers ?? {}).join(", ") || "none"}`,
    `Actions (${Object.keys(def.actions ?? {}).length}): ${Object.keys(def.actions ?? {}).join(", ") || "none"}`,
    "",
    "Use format: \"json\" for the full definition, or restore_flow_version to roll back to it.",
  ].join("\n");
}

export async function handleRestoreFlowVersion(
  api: FlowManagementApi,
  input: unknown,
): Promise<string> {
  const parsed = restoreSchema.parse(input);
  logger.info(
    { flowId: parsed.flowId, versionId: parsed.versionId, confirm: parsed.confirm },
    "Executing restore_flow_version",
  );

  const [version, current] = await Promise.all([
    api.getFlowVersion(parsed.flowId, parsed.versionId, parsed.environment),
    api.getFlow(parsed.flowId, parsed.environment),
  ]);

  const oldActions = Object.keys(current.properties.definition.actions ?? {});
  const newActions = Object.keys(version.properties.definition.actions ?? {});
  const added = newActions.filter((a) => !oldActions.includes(a));
  const removed = oldActions.filter((a) => !newActions.includes(a));

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError([
      `Restoring "${current.properties.displayName}" to version ${parsed.versionId} (created ${version.properties.createdTime ?? "unknown"}) would:`,
      `  actions now: ${oldActions.length} → after restore: ${newActions.length}`,
      added.length > 0 ? `  restored: ${added.join(", ")}` : undefined,
      removed.length > 0 ? `  removed: ${removed.join(", ")}` : undefined,
      "",
      "Nothing has been written. Re-run with confirm=true to apply. The current definition is saved as a new version first, so this is reversible.",
    ]
      .filter((l): l is string => l !== undefined)
      .join("\n"));
  }

  const request: UpdateFlowRequest = {
    properties: {
      displayName: current.properties.displayName,
      definition: version.properties.definition,
      connectionReferences:
        version.properties.connectionReferences ?? current.properties.connectionReferences,
    },
  };
  const updated = await api.updateFlow(parsed.flowId, request, parsed.environment);
  return [
    `Restored "${updated.properties.displayName}" to version ${parsed.versionId}.`,
    `Actions: ${newActions.length}. The pre-restore definition is now itself a version — list_flow_versions to find it if you need to undo this.`,
  ].join("\n");
}

export async function handleGetTriggerInputs(
  api: FlowManagementApi,
  input: unknown,
): Promise<string> {
  const parsed = triggerInputsSchema.parse(input);
  logger.info({ flowId: parsed.flowId, runId: parsed.runId }, "Executing get_trigger_inputs");
  const trigger = await api.getRunTriggerInputs(parsed.flowId, parsed.runId, parsed.environment);

  if (trigger.outputs === undefined) {
    return [
      `Run ${parsed.runId} fired trigger "${trigger.triggerName}" (${trigger.status ?? "unknown status"}) but exposes no payload.`,
      "Manual and recurrence triggers often carry none; connector triggers usually do.",
    ].join("\n");
  }
  const payload = JSON.stringify(trigger.outputs, null, 2);
  const truncated = payload.length > 12_000;
  return [
    `Trigger "${trigger.triggerName}" payload from run ${parsed.runId}${trigger.startTime ? ` (${trigger.startTime})` : ""}:`,
    truncated ? `${payload.slice(0, 12_000)}\n… (truncated)` : payload,
    "",
    "Pass this through test_flow's trigger inputs to replay the run against the same data.",
  ].join("\n");
}

export const flowHistoryHandlers = {
  get_flow_version: handleGetFlowVersion,
  restore_flow_version: handleRestoreFlowVersion,
  get_trigger_inputs: handleGetTriggerInputs,
} as const;
